// @ts-nocheck
import { createStore } from "vuex"
import * as Sentry from "@sentry/nuxt"
import debounce from "~/utils/debounce"
import productStoreModule from "~/store/product"
import orderStoreModule from "~/store/order"
import costCentreModule from "~/store/costCentre"
import addressesModule from "~/store/addresses"
import settingsModule from "~/store/settings"
import integrationsStoreModule from "~/store/integrations"
import xeroStoreModule from "~/store/xero"
import {
	EVENTS,
	track,
	applyUserContext,
	beginImpersonation,
	resetIdentity,
	setPeople,
	incrementPeople,
	emailDomain,
	entityId,
	noteErrorShown,
	entityType,
	enumValue,
	moneyGbp,
	numeric,
	truncate,
	dateOnly,
} from "~/utils/analytics"
import { startImpersonation, clearImpersonation } from "~/utils/impersonation"

export const state = () => ({
	syncHubspotLoading: false,
	makerProfile: false,
	// Compliance fields per maker id, for the makers a user has beyond the one
	// they are working as. The profile endpoint returns makers without
	// attachments, so this is the only way to judge the others.
	makerInsuranceDetails: {},
	allergens: false,
	tags: false,
	attachmentError: false,
	attachmentLoading: false,
	authenticated: false,
	authLoading: false,
	chosenCustomer: false,
	chosenDiscount: false,
	chosenMaker: false,
	chosenBundle: false,
	chosenMakerCategory: false,
	chosenPackage: false,
	chosenProductCategory: false,
	chosenReward: false,
	chosenUser: false,
	cuisines: false,
	customers: false,
	customersLoading: false,
	discounts: false,
	discountList: false,
	discountsLoading: false,
	errorMessages: [],
	successMessages: [],
	messageSeq: 0,
	generatedInvoices: [],
	isAdmin: false,
	isProduction: true,
	loading: 0,
	loginError: false,
	makers: false,
	makersList: false,
	makerLoading: false,
	makersLoading: false,
	makerCategories: false,
	makerCategoriesLite: false,
	makerCatgoriesLoading: false,
	makerReport: false,
	makerReportLoading: false,
	makerDeliveryDays: false,
	newCustomer: false,
	newDiscount: false,
	newMaker: false,
	newBundle: false,
	newPackage: false,
	newMakerCategory: false,
	newProductCategory: false,
	newReward: false,
	newUser: false,
	newVerificationError: null,
	newVerificationSuccess: false,
	verifiedEmail: null,
	verificationType: null,
	packages: false,
	packagesLoading: false,
	pages: false,
	productCategories: false,
	productCategoriesList: false,
	productCategoriesLoading: false,
	rewards: false,
	rewardsLoading: false,
	signUpError: false,
	signUpLoading: false,
	themes: false,
	user: false,
	users: false,
	usersLoading: false,
	weekdays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
	bundlesLoading: false,
	bundles: [],
	organisationError: false,
	organisations: false,
	organisationsLoading: false,
	chosenOrg: false,
	chosenOrgInvoiceConfig: null,
	newOrganisation: false,
	createTeamError: false,
	updateTeamError: false,
	removeFromTeamError: false,
	teamLoading: false,
	resendContractLoading: false,
	resendContractError: false,
	modifierGroupsLoading: false,
	modifierGroups: false,
	updateFeesLoading: false,
	updateFeesError: false,
	serverTime: false,
	subscriptionLoading: false,
	subscriptionConfigurations: false,
	subscriptionTypes: false,
	packageTypes: false,
	chosenSubConfig: false,
	orgPackageChoices: false,
	subConfigLoading: false,

	// ** delivery ** //
	deliveryBooking: false,
})

export const mutations = {
	SET_LOADING(state) {
		state.loading++
	},
	REMOVE_LOADING(state, payload) {
		setTimeout(() => {
			state.loading--
			if (state.loading < 0) {
				state.loading = 0
			}
		}, 1000)
	},
	SET_AUTH_LOADING(state, payload) {
		state.authLoading = payload
	},
	SET_ERROR_MESSAGE(state, payload) {
		if (!payload) return
		const message = typeof payload === "string" ? payload : String(payload)
		// A fresh backend result replaces any lingering banner so stale
		// success/error feedback from a previous interaction doesn't stack.
		state.successMessages = []
		state.errorMessages = [{ id: ++state.messageSeq, message }]
	},
	SET_SUCCESS_MESSAGE(state, payload) {
		if (!payload) return
		const message = typeof payload === "string" ? payload : String(payload)
		state.errorMessages = []
		state.successMessages = [{ id: ++state.messageSeq, message }]
	},
	CLEAR_MESSAGES(state) {
		state.errorMessages = []
		state.successMessages = []
	},
	DISMISS_ERROR_MESSAGE(state, id) {
		state.errorMessages = state.errorMessages.filter((m) => m.id !== id)
	},
	DISMISS_SUCCESS_MESSAGE(state, id) {
		state.successMessages = state.successMessages.filter((m) => m.id !== id)
	},
	SET_GENERATED_INVOICES(state, payload) {
		state.generatedInvoices = payload
	},
	SET_AUTHENTICATED(state, payload) {
		if (payload) {
			const auth_token = useCookie("auth_token", {
				sameSite: true,
				path: "/",
				maxAge: 240 * 60 * 24 * 7,
			})
			auth_token.value = payload.token
			const user_role = useCookie("user_role", {
				sameSite: true,
				path: "/",
				maxAge: 240 * 60 * 24 * 7,
			})
			user_role.value = payload.role
			state.authenticated = true
			state.isAdmin = payload.role === "user"
		} else {
			useCookie("auth_token").value = ""
			useCookie("user_role").value = ""
			state.authenticated = false
			state.isAdmin = false
		}
	},
	SET_SYNC_HUBSPOT_LOADING(state, payload) {
		state.syncHubspotLoading = payload
	},
	SET_PAGES(state, payload) {
		state.pages = payload
	},
	SET_CUISINES(state, payload) {
		if (payload.constructor === Array) {
			state.cuisines = payload
		} else {
			state.cuisines.push(payload)
		}
	},
	SET_ALLERGENS(state, payload) {
		if (payload.constructor === Array) {
			state.allergens = payload
		} else {
			state.allergens.push(payload)
		}
	},
	SET_TAGS(state, payload) {
		if (payload.constructor === Array) {
			state.tags = payload
		} else {
			state.tags.push(payload)
		}
	},
	SET_CUSTOMERS_LOADING(state, payload) {
		state.customersLoading = payload
	},
	SET_CUSTOMERS(state, payload) {
		state.customers = payload
	},
	SET_NEW_CUSTOMER(state, payload) {
		state.newCustomer = payload
	},
	SET_CHOSEN_CUSTOMER(state, payload) {
		state.chosenCustomer = payload
	},
	SET_USER(state, payload) {
		state.user = payload
	},
	SET_USERS(state, payload) {
		state.users = payload
	},
	SET_NEW_USER(state, payload) {
		state.newUser = payload
	},
	SET_NEW_VERIFICATION_SUCCESS(state, payload) {
		state.newVerificationSuccess = payload
	},
	SET_NEW_VERIFICATION_ERROR(state, payload) {
		state.newVerificationError = payload
	},
	SET_VERIFY_SUCCESS(state, payload) {
		state.verifySuccess = payload
	},
	SET_VERIFY_ERROR(state, payload) {
		state.verifyError = payload
	},
	SET_VERIFIED_EMAIL(state, payload) {
		state.verifiedEmail = payload
	},
	SET_VERIFICATION_TYPE(state, payload) {
		state.verificationType = payload
	},
	SET_CHOSEN_USER(state, payload) {
		state.chosenUser = payload
	},
	SET_LOGIN_ERROR(state, payload) {
		state.loginError = payload
	},
	SET_SIGN_UP_ERROR(state, payload) {
		state.signUpError = payload
	},
	SET_SIGN_UP_LOADING(state, payload) {
		state.signUpLoading = payload
	},
	SET_MAKER_REPORT_LOADING(state, payload) {
		state.makerReportLoading = payload
	},
	SET_MAKER_REPORT(state, payload) {
		state.makerReport = payload
	},
	SET_MAKERS_LOADING(state, payload) {
		state.makersLoading = payload
	},
	SET_MAKER_LOADING(state, payload) {
		state.makerLoading = payload
	},
	SET_MAKERS(state, payload) {
		if (state.user && !state.isAdmin) {
			const makerIds = state.user.makers.map((maker) => maker.id)
			state.makers = payload.filter((maker) => makerIds.includes(maker.id))
		} else {
			state.makers = payload
		}
	},
	SET_MAKER_PROFILE(state, payload) {
		state.makerProfile = payload
	},
	SET_MAKER_INSURANCE_DETAILS(state, payload) {
		state.makerInsuranceDetails = { ...state.makerInsuranceDetails, ...payload }
	},
	SET_MAKERS_LIST(state, payload) {
		if (state.isAdmin) {
			state.makersList = payload
		} else {
			state.makersList = payload.filter((maker) =>
				state.user.makers.some((item) => item.id === maker.id)
			)
		}
	},
	SET_CHOSEN_MAKER(state, payload) {
		state.chosenMaker = {
			...payload,
			deliveryDays: payload.deliveryDays?.sort((a, b) => {
				const aIndex = state.weekdays.indexOf(a.name)
				const bIndex = state.weekdays.indexOf(b.name)
				return aIndex - bIndex
			}),
		}
	},
	// Replaces the chosen maker's contract records with what the backend just returned. The
	// contract signed state is always read back off the record, never tracked in the component.
	SET_CHOSEN_MAKER_CONTRACTS(state, payload) {
		if (!state.chosenMaker) return
		state.chosenMaker = {
			...state.chosenMaker,
			MakerContracts: payload,
		}
	},
	// Same job for the makers list: replace one maker's contract records with what the backend
	// returned, so the table's `contract_signed` column re-derives instead of being set locally.
	SET_MAKER_CONTRACTS_IN_LIST(state, { makerId, MakerContracts }) {
		const maker = (state.makers || []).find((m) => m.id === makerId)
		if (!maker) return
		maker.MakerContracts = MakerContracts
	},
	SET_NEW_MAKER(state, payload) {
		state.newMaker = payload
	},
	SET_NEW_SPECIAL_DATE(state, payload) {
		state.chosenMaker?.specialDeliveryDays.push(payload)
	},
	SET_MAKER_CATEGORIES_LOADING(state, payload) {
		state.makerCategoriesLoading = payload
	},
	SET_MAKER_CATEGORIES(state, payload) {
		state.makerCategories = payload
	},
	SET_MAKER_CATEGORIES_LITE(state, payload) {
		state.makerCategoriesLite = payload
	},
	SET_NEW_MAKER_CATEGORY(state, payload) {
		state.newMakerCategory = payload
	},
	SET_MAKERS_DELIVERY_DAYS(state, payload) {
		state.makerDeliveryDays = payload
	},
	SET_CHOSEN_MAKER_CATEGORY(state, payload) {
		state.chosenMakerCategory = payload
	},
	SET_PACKAGES_LOADING(state, payload) {
		state.packagesLoading = payload
	},
	SET_CHOSEN_PACKAGE(state, payload) {
		state.chosenPackage = payload
	},
	SET_NEW_PACKAGE(state, payload) {
		state.newPackage = payload
	},
	SET_PACKAGES(state, payload) {
		state.packages = payload
	},
	SET_BUNDLES_LOADING(state, payload) {
		state.bundlesLoading = payload
	},
	SET_CHOSEN_BUNDLE(state, payload) {
		state.chosenBundle = payload
	},
	SET_NEW_BUNDLE(state, payload) {
		state.newBundle = payload
	},
	SET_BUNDLES(state, payload) {
		state.bundles = payload
	},
	SET_PRODUCT_CATEGORIES_LOADING(state, payload) {
		state.productCategoriesLoading = payload
	},
	SET_NEW_PRODUCT_CATEGORIES_SORT(state, payload) {
		const newOrder = []
		payload.forEach((dataItem) => {
			newOrder.push(
				state.chosenMaker.productCategories.find(
					(productCategory) => productCategory.id === dataItem.id
				)
			)
		})
		state.chosenMaker.productCategories = newOrder
	},
	SET_NEW_MAKER_CATEGORIES_SORT(state, payload) {
		const newOrder = []
		payload.forEach((dataItem) => {
			newOrder.push(
				state.chosenMaker.makersCategories.find((makerCategory) => makerCategory.id === dataItem.id)
			)
		})
		state.chosenMaker.makersCategories = newOrder
	},
	SET_PRODUCT_CATEGORIES(state, payload) {
		state.productCategories = payload
	},
	SET_PRODUCT_CATEGORIES_LIST(state, payload) {
		state.productCategoriesList = payload
	},
	SET_NEW_PRODUCT_CATEGORY(state, payload) {
		state.newProductCategory = payload
	},
	SET_CHOSEN_PRODUCT_CATEGORY(state, payload) {
		state.chosenProductCategory = payload
	},
	SET_DISCOUNTS_LOADING(state, payload) {
		state.discountsLoading = payload
	},
	SET_DISCOUNTS(state, payload) {
		state.discounts = payload
		state.discountList = payload
			.filter((item) => item.is_active)
			.map((discount) => {
				return { id: discount.id, name: discount.code }
			})
	},
	SET_NEW_DISCOUNT(state, payload) {
		state.newDiscount = payload
	},
	SET_CHOSEN_DISCOUNT(state, payload) {
		state.chosenDiscount = payload
	},
	SET_REWARDS_LOADING(state, payload) {
		state.rewardsLoading = payload
	},
	SET_REWARDS(state, payload) {
		state.rewards = payload
	},
	SET_NEW_REWARD(state, payload) {
		state.newReward = payload
	},
	SET_CHOSEN_REWARD(state, payload) {
		state.chosenReward = payload
	},
	SET_ATTACHMENT_ERROR(state, payload) {
		state.attachmentError = payload
	},
	SET_ATTACHMENT_LOADING(state, payload) {
		state.attachmentLoading = payload
	},
	SET_ENTITY_STATE(state, payload) {
		const entityMap = { products: state.product?.products }
		const list = entityMap[payload.entity] || state[payload.entity]
		if (!list) return
		const index = list.findIndex((entity) => entity.id === payload.id)
		if (index === -1) return
		list[index][payload.field] =
			typeof payload.setValue === "boolean" ? !list[index][payload.field] : payload.setValue
	},
	SET_ORGANISATION_ERROR(state, payload) {
		state.organisationError = payload
	},
	SET_ORGANISATIONS_LOADING(state, payload) {
		state.organisationsLoading = payload
	},
	SET_ORGANISATIONS(state, payload) {
		state.organisations = payload
	},
	SET_CHOSEN_ORGANISATION(state, payload) {
		state.chosenOrg = payload
	},
	SET_CHOSEN_ORG_INVOICE_CONFIG(state, payload) {
		state.chosenOrgInvoiceConfig = payload
	},
	SET_NEW_ORGANISATION(state, payload) {
		state.newOrganisation = payload
	},
	SET_CREATE_TEAM_ERROR(state, payload) {
		state.createTeamError = payload
	},
	SET_UPDATE_TEAM_ERROR(state, payload) {
		state.updateTeamError = payload
	},
	SET_REMOVE_FROM_TEAM_ERROR(state, payload) {
		state.removeFromTeamError = payload
	},
	SET_TEAM_LOADING(state, payload) {
		state.teamLoading = payload
	},
	SET_RESEND_CONTRACT_LOADING(state, payload) {
		state.resendContractLoading = payload
	},
	SET_RESEND_CONTRACT_ERROR(state, payload) {
		state.resendContractError = payload
	},
	SET_MODIFIER_GROUPS(state, payload) {
		state.modifierGroups = payload
	},
	SET_MODIFIER_GROUPS_LOADING(state, payload) {
		state.modifierGroupsLoading = payload
	},
	SET_UPDATE_FEES_LOADING(state, payload) {
		state.updateFeesLoading = payload
	},
	SET_UPDATE_FEES_ERROR(state, payload) {
		state.updateFeesError = payload
	},
	SET_SUBSCRIPTION_LOADING(state, payload) {
		state.subscriptionLoading = payload
	},
	SET_SUBSCRIPTION_CONFIGURATIONS(state, payload) {
		state.subscriptionConfigurations = payload
	},
	SET_SUBSCRIPTION_TYPES(state, payload) {
		state.subscriptionTypes = payload
	},
	SET_SERVER_TIME(state, payload) {
		state.serverTime = payload
	},
	SET_PACKAGE_TYPES(state, payload) {
		state.packageTypes = payload
	},
	SET_CHOSEN_SUB_CONFIG(state, payload) {
		state.chosenSubConfig = payload
	},
	SET_SUB_CONFIG_LOADING(state, payload) {
		state.subConfigLoading = payload
	},
	SET_ORG_PACKAGE_CHOICES(state, payload) {
		state.orgPackageChoices = payload
	},

	// ** delivery ** //
	SET_DELIVERY_BOOKING(state, payload) {
		state.deliveryBooking = payload
	},
}

// Lands an order response's active booking on BOTH places the hub keeps a copy, from one
// response: `chosenOrder.delivery` drives the booking card's pill, rows and tracking link,
// root `deliveryBooking` drives BookDelivery's Book/Cancel/Close buttons and the View Booking
// modal. Shared by the poller's read and the manual Refresh so they cannot disagree.
//
// Commits only while the page still shows the order the response is for (a slow call for
// order A must not land on order B), and never an older copy of the row already on screen.
// updatedAt is a second-precision DATETIME string, so string order is time order.
// Returns the booking uncommitted in both cases rather than null: null reads to the poller
// as "gone". Null only when a 2xx somehow carries no booking.
function commitDeliveryBooking(commit, rootState, response) {
	const delivery = response?.delivery
	if (!delivery) return null

	if (rootState.order?.chosenOrder?.id !== response.id) return delivery
	const current = rootState.order.chosenOrder.delivery
	if (current?.id === delivery.id && delivery.updatedAt < current.updatedAt) return delivery

	commit("SET_DELIVERY_BOOKING", delivery)
	commit("order/SET_CHOSEN_ORDER_DELIVERY", delivery, { root: true })

	return delivery
}

export const actions = {
	async logInUser({ commit }, payload) {
		commit("SET_AUTH_LOADING", true)
		// Nothing cleared this, so the banner from a debounce-truncated attempt
		// stayed on screen through the retry that actually worked. Ticket 1143.
		commit("SET_LOGIN_ERROR", false)

		const body = JSON.stringify(payload.body)
		const config = {
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			method: "POST",
			body,
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/users/login`, config)
			.then((response) => {
				commit("SET_AUTH_LOADING", false)
				commit("SET_AUTHENTICATED", response)
				// A fresh login is a different human than whoever used this browser
				// last, so tear the old identity down completely before rebuilding.
				// resetIdentity() (not just clearImpersonation()) is required: the
				// impersonation block lives in module memory, and the 401 path in
				// handleError navigates to /login without resetting anything — so a
				// stale block would otherwise survive into this new session and
				// silently suppress the real user's profile for its whole lifetime.
				resetIdentity()
				clearImpersonation()
				track(EVENTS.LOGIN_SUCCEEDED, {
					role: response?.role,
					is_admin: response?.role === "user",
				})
				navigateTo(payload.redirect)
			})
			.catch((error) => {
				commit("SET_AUTH_LOADING", false)
				let failureReason = "network"
				if (error.response) {
					const status = error.response.status
					switch (status) {
						case 400:
							commit("SET_LOGIN_ERROR", "Invalid credentials.")
							failureReason = "invalid_credentials"
							break
						case 403:
							commit("SET_LOGIN_ERROR", "Access forbidden. User account not activated.")
							failureReason = "not_activated"
							break
						case 404:
							commit("SET_LOGIN_ERROR", "Invalid credentials.")
							failureReason = "invalid_credentials"
							break
						case 500:
							commit("SET_LOGIN_ERROR", "Internal server error. Please try again later.")
							failureReason = "server_error"
							break
						default:
							commit("SET_LOGIN_ERROR", error.message)
							failureReason = "unknown"
							break
					}
				} else {
					commit(
						"SET_LOGIN_ERROR",
						"An unexpected error occurred. Please try again later or contact our service."
					)
				}
				track(EVENTS.LOGIN_FAILED, {
					http_status: numeric(error.response?.status),
					failure_reason: failureReason,
					// Domain only. The address itself is never an event property.
					attempt_email_domain: emailDomain(payload?.body?.email),
				})
			})
	},
	logoutUser({ commit, dispatch }, payload) {
		// Tracked before reset() so the event still lands on the outgoing identity.
		track(EVENTS.LOGOUT_PERFORMED, { logout_kind: payload?.logoutKind ?? "manual" })
		resetIdentity()
		clearImpersonation()
		commit("SET_AUTHENTICATED", false)
		commit("SET_MAKER_PROFILE", false)
		commit("SET_CHOSEN_MAKER", false)
		commit("order/SET_CHOSEN_ORDER", false)
		dispatch("order/resetLedgers")
		navigateTo("/login")
	},
	async refreshToken({ commit }, payload) {
		if (!payload) throw new Error("No token")
		const response = await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/refresh`, {
			method: "POST",
			headers: {
				"x-access-token-user": payload,
			},
		})
		return response.token
	},
	getShareCart({ dispatch }, id) {
		return $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/shareCart/${id}`, {})
	},
	async getUsers({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const page = payload?.page || 1
		const limit = payload?.limit || 20
		const query = `?limit=${limit}&page=${page}${filterQuery}`
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users${query}`, config)
			.then((response) => {
				commit("SET_USERS", response.users)
				commit("SET_PAGES", response.paginate)
				commit("SET_AUTH_LOADING", false)
			})
			.catch((error) => {
				commit("SET_AUTH_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	filterUsers: debounce(async function ({ commit, dispatch }, payload) {
		const filters = payload?.filters || {}
		const page = payload?.page || 1
		const limit = payload?.limit || 20

		commit("SET_AUTH_LOADING", true)

		const config = {
			method: "POST",
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			body: JSON.stringify({
				filters: filters,
				page,
				limit,
			}),
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/filterUsers`, config)
			.then((response) => {
				commit("SET_USERS", response.users)
				commit("SET_PAGES", response.paginate)
				commit("SET_AUTH_LOADING", false)
			})
			.catch((error) => {
				commit("SET_AUTH_LOADING", false)
				dispatch("handleError", { error })
			})
	}, 200),
	async getUser({ commit, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/profile`, config)
			.then((response) => {
				commit("SET_USER", response)

				// The only place the full profile lands, so the only honest place to
				// identify. While impersonating, applyUserContext keeps the admin's
				// distinct_id and skips the profile write entirely — this response is
				// the TARGET's profile, not the acting human's.
				//
				// Deliberately NOT ending impersonation here, even when the profile is
				// the impersonator's own. The record is shared across tabs, so clearing
				// it from one tab drops the guard for a tab that may still hold the
				// target's token. There is no "stop impersonating" control in hub — the
				// only way back to your own token is logging out, and both logoutUser
				// and logInUser call resetIdentity(), which lifts the block. So the
				// lifecycle is already closed; see store/__tests__/analyticsIdentity.
				applyUserContext(response)

				if (response.role === "maker") {
					commit("SET_MAKERS", response.makers)
					if (response.makers.length > 0) {
						commit("SET_MAKER_PROFILE", response.makers[0])
						dispatch("getMakerById", response.makers[0].id)
						// makers[0] is covered by getMakerById above; the rest need their
						// own fetch before their compliance state is known.
						dispatch("getMakerInsuranceDetails", response.makers.slice(1))
					}
				}
			})
			.catch((error) => {
				dispatch("logoutUser", { logoutKind: "profile_fetch_failed" })
				dispatch("handleError", { error })
			})
	},
	// Compliance fields for a user's other makers. Deliberately quiet: if one of
	// these fetches fails the reminder just stays silent for that maker, which
	// beats logging someone out or dropping an error banner on the home page.
	async getMakerInsuranceDetails({ commit }, makers) {
		if (!Array.isArray(makers) || !makers.length) {
			return
		}
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		const entries = await Promise.all(
			makers.map(async (maker) => {
				if (!maker?.id) {
					return null
				}
				try {
					const response = await $fetch(
						`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/${maker.id}`,
						config
					)
					return [
						String(maker.id),
						{
							id: response.id ?? maker.id,
							attachments: response.attachments,
							insurance_expiry_date: response.insurance_expiry_date,
						},
					]
				} catch {
					return null
				}
			})
		)

		const loaded = Object.fromEntries(entries.filter(Boolean))
		if (Object.keys(loaded).length) {
			commit("SET_MAKER_INSURANCE_DETAILS", loaded)
		}
	},
	async getUserById({ commit, dispatch, state }, id) {
		try {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}
			const response = await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/${id}`, config)
			if (response.is_active === false && !state.isAdmin) {
				setTimeout(async () => {
					try {
						await dispatch("logoutUser")
					} catch (logoutError) {
						await dispatch("handleError", { error: logoutError })
					}
				}, 5000)
				throw new Error("User is inactive, logging out")
			}
			commit("SET_CHOSEN_USER", response)
		} catch (error) {
			await dispatch("handleError", { error })
		}
	},
	async updateUser({ state, commit, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body: JSON.stringify(state.chosenUser),
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/${state.chosenUser.id}`, config)
			.then(() => {
				track(EVENTS.USER_SAVED, {
					operation: "updated",
					target_user_id: state.chosenUser?.id,
					target_role: enumValue(state.chosenUser?.role),
					is_active: Boolean(state.chosenUser?.is_active),
				})
				commit("SET_SUCCESS_MESSAGE", "Successfully updated user")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 5000)
			})
			.catch((error) => {
				dispatch("handleError", { error, description: "updated user" })
			})
	},
	async verifyUser({ commit }, payload) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
		}
		try {
			const response = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/public/users/verify/${payload.verificationId}`,
				config
			)
			track(EVENTS.ACCOUNT_VERIFIED, {
				verification_type: enumValue(response?.type),
				verification_result: "success",
			})
			commit("SET_VERIFY_SUCCESS", true)
			commit("SET_VERIFY_ERROR", null)
			commit("SET_VERIFIED_EMAIL", response.email || null)
			commit("SET_VERIFICATION_TYPE", response.type || null)
			return response
		} catch (error) {
			commit("SET_VERIFY_SUCCESS", false)
			let errorMessage = "An error occurred during verification."
			if (error.response) {
				if (error.response) {
					const data = error.response._data
					if (data?.message === "Verification code expired") {
						errorMessage = "Verification code has expired"
					} else if (data?.error === "Invalid verification link") {
						errorMessage = "Invalid or already used verification link"
					} else if (data?.message) {
						errorMessage = data.message
					}
				}
			}
			commit("SET_VERIFY_ERROR", errorMessage)
			commit("SET_VERIFIED_EMAIL", error.response?._data?.email || null)
			commit("SET_VERIFICATION_TYPE", error.response?._data?.type || null)
			throw error
		}
	},
	async generateNewVerification({ commit }, payload) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "POST",
			body: JSON.stringify({ email: payload.email, type: payload.type }),
		}

		try {
			const response = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/public/users/emailChangeVerification`,
				config
			)
			commit("SET_NEW_VERIFICATION_SUCCESS", true)
			commit("SET_NEW_VERIFICATION_ERROR", null)
			return response
		} catch (error) {
			commit("SET_NEW_VERIFICATION_SUCCESS", false)
			let errorMessage = "Failed to generate new verification"

			if (error.response) {
				if (error.response.status === 429) {
					errorMessage = error.response._data || "Verification code already sent, please wait"
				} else if (error.response.status === 403) {
					errorMessage = error.response._data?.error || "User is already verified and active"
				} else {
					errorMessage = error.response._data?.error || error.response._data || errorMessage
				}
			}
			commit("SET_NEW_VERIFICATION_ERROR", errorMessage)
			throw error
		}
	},
	async loginAsUser({ state, commit, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/loginAsUser/${state.chosenUser.id}`,
			config
		)
			.then((response) => {
				// Recorded BEFORE SET_AUTHENTICATED swaps the cookie, while state.user
				// is still the admin. Without this, getUser returns the target's
				// profile and the admin's clicks get attributed to them.
				//
				// beginImpersonation() must be called HERE, synchronously: unlike
				// loginAsMaker this navigates client-side (navigateTo below), so the
				// plugin never re-runs and the boot-time guard would never fire —
				// the next getUser would identify() the target and overwrite their
				// People profile. localStorage still carries it across a later reload.
				const impersonationCtx = {
					impersonator_user_id: state.user?.id,
					impersonation_kind: "user" as const,
					impersonated_user_id: state.chosenUser?.id,
				}
				// Fail closed. Without a persisted record a later reload boots the
				// target's token with no guard, and getUser would identify the target
				// and overwrite their People profile. Better to refuse the switch.
				if (!startImpersonation(impersonationCtx)) {
					commit(
						"SET_ERROR_MESSAGE",
						"Couldn't switch user — browser storage is unavailable, so the session couldn't be tracked safely."
					)
					return
				}
				// After the guard (an aborted switch must not count) but before
				// beginImpersonation, while the admin is still the identified user.
				incrementPeople({ impersonations_started_count: 1 })
				beginImpersonation(impersonationCtx)
				track(
					EVENTS.IMPERSONATION_STARTED,
					{
						impersonation_kind: "user",
						target_user_id: state.chosenUser?.id,
						target_role: state.chosenUser?.role,
					},
					{ beacon: true }
				)
				commit("SET_AUTHENTICATED", response)
				navigateTo("/")
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	async loginAsMaker({ state, commit, dispatch }, id) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/loginAsMaker/${id}`, config)
			.then((response) => {
				// Belt and braces: loginAsMaker reloads, so the plugin's boot-time
				// guard would catch this anyway — but blocking here too means
				// neither impersonation path depends on whether it reloads.
				const impersonationCtx = {
					impersonator_user_id: state.user?.id,
					impersonation_kind: "maker" as const,
					impersonated_maker_id: entityId(id),
				}
				// Fail closed, and it matters more here: the reload below discards the
				// in-memory block, so the persisted record is the ONLY thing standing
				// between the plugin and identifying the target.
				if (!startImpersonation(impersonationCtx)) {
					commit(
						"SET_ERROR_MESSAGE",
						"Couldn't switch to this maker — browser storage is unavailable, so the session couldn't be tracked safely."
					)
					return
				}
				// After the guard, before the block — same reasoning as loginAsUser.
				incrementPeople({ impersonations_started_count: 1 })
				beginImpersonation(impersonationCtx)
				// sendBeacon: window.location.reload() below would otherwise kill the
				// in-flight request.
				track(
					EVENTS.IMPERSONATION_STARTED,
					{ impersonation_kind: "maker", target_maker_id: entityId(id) },
					{ beacon: true }
				)
				commit("SET_AUTHENTICATED", response)
				window.location.reload()
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	async loginAsCustomer({ state, commit, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/loginAsCustomer/${state.chosenCustomer.id}`,
			config
		)
			.then((response) => {
				let redirect = "/search"
				if (response.subscription) {
					redirect = "/account/premium"
				}
				// No impersonation record, and deliberately NO beginImpersonation():
				// this opens the customer STORE in a new tab and never commits
				// SET_AUTHENTICATED, so the admin stays the authenticated hub user.
				// Blocking here (as both PR review bots suggested "for symmetry")
				// would permanently suppress the ADMIN's own profile writes for the
				// rest of their session, for no protective benefit.
				track(EVENTS.IMPERSONATION_STARTED, {
					impersonation_kind: "customer",
					target_customer_id: entityId(state.chosenCustomer?.id),
					has_subscription: Boolean(response.subscription),
				})
				incrementPeople({ impersonations_started_count: 1 })
				const redirectUrl = `${import.meta.env.VITE_AND_DINE_STORE}${redirect}?loginAs=${encodeURIComponent(JSON.stringify(response))}`
				window.open(redirectUrl, "_blank")
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	async createUser({ commit, dispatch }, payload) {
		commit("SET_SIGN_UP_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "POST",
			body: JSON.stringify(payload),
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users`, config)
			.then((response) => {
				// target_role "maker" creations are the supply-onboarding counter.
				track(EVENTS.USER_SAVED, {
					operation: "created",
					target_user_id: response?.id,
					target_role: enumValue(payload?.role),
				})
				commit("SET_USER", response)
				commit("SET_SIGN_UP_LOADING", false)
				navigateTo(`/users`)
			})
			.catch((error) => {
				commit("SET_SIGN_UP_LOADING", false)
				commit("SET_SIGN_UP_ERROR", error.response)
				dispatch("handleError", { error, description: "create user" })
			})
	},
	async forgotPassword({ commit, dispatch }, payload) {
		commit("SET_AUTH_LOADING", true)
		commit("SET_SIGN_UP_ERROR", false)
		const body = JSON.stringify(payload)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "POST",
			body,
		}

		return await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/public/users/forgotPassword`,
			config
		)
			.then(() => {
				// Half of the reset funnel; a low completion rate means the reset
				// email is broken. Domain only — never the address.
				track(EVENTS.PASSWORD_RESET_REQUESTED, {
					email_domain: emailDomain(payload?.email),
				})
				commit("SET_AUTH_LOADING", false)
				return true
			})
			.catch((error) => {
				commit("SET_AUTH_LOADING", false)
				if (error.response) {
					switch (error.response.status) {
						case 404:
							commit("SET_SIGN_UP_ERROR", "No account found with that email address.")
							break
						case 500:
							commit("SET_SIGN_UP_ERROR", "Internal server error. Please try again later.")
							break
						default:
							commit("SET_SIGN_UP_ERROR", error.message)
							break
					}
				} else {
					commit(
						"SET_SIGN_UP_ERROR",
						"An unexpected error occurred. Please try again later or contact our service."
					)
				}
				dispatch("handleError", { error })
				return false
			})
	},
	async updatePassword({ commit }, payload) {
		const body = JSON.stringify(payload)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/users/resetPassword`, config)
			.then(() => {
				navigateTo(`/`)
			})
			.catch((error) => {
				// This used to be an empty catch, so a rejected reset did nothing at
				// all on screen. The backend now rejects a mismatched pair, a missing
				// confirmation or an under-8-character password with a 400
				// (and-dine-backend#618), and this page also serves account
				// activation — failing silently there is how someone ends up unable
				// to set a password with no idea why. Let the caller render it.
				throw error
			})
	},
	async getCuisines({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const query = `?limit=999${filterQuery}`
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-customer": useCookie("auth_token").value,
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/cuisines${query}`, config)
			.then((response) => {
				commit("SET_CUISINES", response.models)
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	async createCuisines({ commit, dispatch }, payload) {
		const body = JSON.stringify({ ...payload, is_active: true })
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "POST",
			body,
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/cuisines`, config)
			.then((response) => {
				track(EVENTS.CATALOGUE_ENTITY_SAVED, {
					entity_type: "cuisine",
					operation: "created",
				})
				commit("SET_CUISINES", response)
				commit("SET_SUCCESS_MESSAGE", "Successfully created cuisine")
			})
			.catch((error) => {
				dispatch("handleError", { error, description: "create cuisine" })
			})
	},
	async getAllergens({ commit, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-customer": useCookie("auth_token").value,
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/allergens?limit=999`, config)
			.then((response) => {
				commit("SET_ALLERGENS", response.allergens)
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	async getTags({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const query = `?limit=999${filterQuery}`

		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-customer": useCookie("auth_token").value,
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/tags${query}`, config)
			.then((response) => {
				commit("SET_TAGS", response.tags)
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	async createTag({ commit, dispatch }, payload) {
		const body = JSON.stringify({ ...payload, is_active: true })
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "POST",
			body,
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/tags`, config)
			.then((response) => {
				commit("SET_TAGS", response)
			})
			.catch((error) => {
				dispatch("handleError", { error, description: "create tag" })
			})
	},
	getCustomers: debounce(async function ({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const page = payload?.page || 1
		const limit = payload?.limit || 20
		const query = `?limit=${limit}&page=${page}${filterQuery}`
		!payload && commit("SET_CUSTOMERS_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/customers${query}`, config)
			.then((response) => {
				commit("SET_CUSTOMERS", response.customers)
				commit("SET_PAGES", response.paginate)
				commit("SET_CUSTOMERS_LOADING", false)
			})
			.catch((error) => {
				commit("SET_CUSTOMERS_LOADING", false)
				dispatch("handleError", { error })
			})
	}, 200),
	async getCustomerById({ commit, dispatch }, id) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/customers/${id}`, config)
			.then((response) => {
				commit("SET_CHOSEN_CUSTOMER", response)
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	filterCustomers: debounce(async function ({ commit, dispatch }, payload) {
		const filters = payload?.filters || {}
		const page = payload?.page || 1
		const limit = payload?.limit || 20

		commit("SET_CUSTOMERS_LOADING", true)

		const config = {
			method: "POST",
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			body: JSON.stringify({
				filters: filters,
				page,
				limit,
			}),
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/customers/filterCustomers`, config)
			.then((response) => {
				commit("SET_CUSTOMERS", response.customers)
				commit("SET_PAGES", response.paginate)
				commit("SET_CUSTOMERS_LOADING", false)
			})
			.catch((error) => {
				commit("SET_CUSTOMERS_LOADING", false)
				dispatch("handleError", { error })
			})
	}, 200),
	async updateCustomer({ state, commit, dispatch }) {
		const body = JSON.stringify(state.chosenCustomer)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/customers/${state.chosenCustomer.id}`,
			config
		)
			.then(() => {
				track(EVENTS.CUSTOMER_SAVED, {
					customer_id: entityId(state.chosenCustomer?.id),
					organisation_id: entityId(state.chosenCustomer?.organisation_id),
					// `role`, not `customerRole`: the customers edit form binds the
					// dropdown with `id: "role"` straight onto chosenCustomer[input.id]
					// (pages/customers/[id].vue:157, :203). There is no customerRole
					// field on the model, so every customer_saved event was dropping it.
					customer_role: enumValue(state.chosenCustomer?.role),
				})
				commit("SET_SUCCESS_MESSAGE", "Successfully updated customer")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 5000)
			})
			.catch((error) => {
				dispatch("handleError", { error, description: "updated customer" })
			})
	},
	async getRewards({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const page = payload?.page || 1
		const limit = payload?.limit || 20
		const orderBy = payload?.orderBy || ""
		const orderDirection = payload?.orderDirection || "DESC"
		const query = `?limit=${limit}&page=${page}${filterQuery}${orderBy ? `&orderBy=${orderBy}&orderDirection=${orderDirection}` : ""}`
		!payload && commit("SET_REWARDS_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/rewards${query}`, config)
			.then((response) => {
				commit("SET_REWARDS", response.rewards)
				commit("SET_PAGES", response.paginate)
				commit("SET_REWARDS_LOADING", false)
			})
			.catch((error) => {
				commit("SET_REWARDS_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async getRewardById({ commit, dispatch }, id) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/rewards/${id}`, config)
			.then((response) => {
				commit("SET_CHOSEN_REWARD", response)
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	async updateReward({ state, commit, dispatch }) {
		const body = JSON.stringify(state.chosenReward)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/rewards/${state.chosenReward.id}`,
			config
		)
			.then(() => {
				track(EVENTS.CATALOGUE_ENTITY_SAVED, {
					entity_type: "reward",
					operation: "updated",
				})
				commit("SET_SUCCESS_MESSAGE", "Successfully updated reward")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 5000)
			})
			.catch((error) => {
				dispatch("handleError", { error, description: "updated reward" })
			})
	},
	async createReward({ state, commit, dispatch }) {
		const body = JSON.stringify(state.newReward)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "POST",
			body,
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/rewards`, config)
			.then(() => {
				dispatch("getRewards")
				navigateTo(`/rewards`)
			})
			.catch((error) => {
				dispatch("handleError", { error, description: "create reward" })
			})
	},
	async getDiscounts({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const page = payload?.page || 1
		const limit = payload?.limit || 999
		const orderBy = payload?.orderBy || ""
		const orderDirection = payload?.orderDirection || "DESC"
		const query = `?limit=${limit}&page=${page}${filterQuery}${orderBy ? `&orderBy=${orderBy}&orderDirection=${orderDirection}` : ""}`
		!payload && commit("SET_DISCOUNTS_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/discounts${query}`, config)
			.then((response) => {
				commit("SET_DISCOUNTS", response.discounts)
				commit("SET_PAGES", response.paginate)
				commit("SET_DISCOUNTS_LOADING", false)
			})
			.catch((error) => {
				commit("SET_DISCOUNTS_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async getDiscountById({ commit, dispatch }, id) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/discounts/${id}`, config)
			.then((response) => {
				commit("SET_CHOSEN_DISCOUNT", response)
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	async updateDiscount({ state, commit, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		const body = JSON.stringify({
			...state.chosenDiscount,
			minimum_value: state.chosenDiscount.minimum_value,
		})

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/discounts/${state.chosenDiscount.id}`,
			{ method: "PUT", body, ...config }
		)
			.then(() => {
				track(EVENTS.CATALOGUE_ENTITY_SAVED, {
					entity_type: "discount",
					operation: "updated",
				})
				commit("SET_SUCCESS_MESSAGE", "Successfully updated discount")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 5000)
			})
			.catch((error) => {
				dispatch("handleError", { error, description: "updated discount" })
			})
	},
	async createDiscount({ state, commit, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		const body = JSON.stringify({
			...state.newDiscount,
			minimum_value: state.newDiscount.minimum_value,
			discount_usage: "regular",
		})

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/discounts`, {
			method: "POST",
			body,
			...config,
		})
			.then(() => {
				dispatch("getDiscounts")
				navigateTo(`/discounts`)
			})
			.catch((error) => {
				dispatch("handleError", { error, description: "create discount" })
			})
	},
	async getMakerCategories({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const page = payload?.page || 1
		const limit = payload?.limit || 999
		const orderBy = payload?.orderBy || ""
		const orderDirection = payload?.orderDirection || "DESC"
		const query = `?limit=${limit}&page=${page}${filterQuery}${orderBy ? `&orderBy=${orderBy}&orderDirection=${orderDirection}` : ""}`
		!payload && commit("SET_MAKER_CATEGORIES_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/makerCategories${query}`, config)
			.then((response) => {
				commit(
					payload?.lite ? "SET_MAKER_CATEGORIES_LITE" : "SET_MAKER_CATEGORIES",
					response.models
				)
				commit("SET_PAGES", response.paginate)
				commit("SET_MAKER_CATEGORIES_LOADING", false)
			})
			.catch((error) => {
				commit("SET_MAKER_CATEGORIES_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async getMakerCategoryById({ commit, dispatch }, id) {
		commit("SET_MAKER_CATEGORIES_LOADING", true)

		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/makerCategories/${id}`, config)
			.then((response) => {
				commit("SET_CHOSEN_MAKER_CATEGORY", response)
				commit("SET_MAKER_CATEGORIES_LOADING", false)
			})
			.catch((error) => {
				commit("SET_MAKER_CATEGORIES_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async updateMakerCategory({ commit, state, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		const body = JSON.stringify(state.chosenMakerCategory)

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/makerCategories/${
				state.chosenMakerCategory.id
			}`,
			{
				method: "PUT",
				body,
				...config,
			}
		)
			.then(() => {
				track(EVENTS.CATALOGUE_ENTITY_SAVED, {
					entity_type: "maker_category",
					operation: "updated",
				})
				commit("SET_SUCCESS_MESSAGE", "Successfully updated Maker category")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 5000)
			})
			.catch((error) => {
				dispatch("handleError", {
					error,
					description: "updated maker category",
				})
			})
	},
	async createMakerCategory({ commit, dispatch }, payload) {
		commit("SET_MAKER_CATEGORIES_LOADING", true)

		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/makerCategories`, {
			method: "POST",
			body: payload.body,
			...config,
		})
			.then(() => {
				track(EVENTS.CATALOGUE_ENTITY_SAVED, {
					entity_type: "maker_category",
					operation: "created",
				})
				commit("SET_MAKER_CATEGORIES_LOADING", false)
				commit("SET_SUCCESS_MESSAGE", "Successfully created maker category")
				payload.redirect && navigateTo(`/maker-categories`)
			})
			.catch((error) => {
				commit("SET_MAKER_CATEGORIES_LOADING", false)
				dispatch("handleError", { error, description: "create maker category" })
			})
	},
	// Makers export is now generated asynchronously via the file-generation queue;
	// the backend emails a download link when ready.
	async downloadMakersCsv({ dispatch, commit }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/exportCsv`, config)
			.then(() => {
				commit(
					"SET_SUCCESS_MESSAGE",
					"Report queued — we'll email you a download link when it's ready."
				)
				setTimeout(() => commit("SET_SUCCESS_MESSAGE", false), 5000)
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	async getMakers({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const page = payload?.page || 1
		const limit = payload?.limit || 20
		const orderBy = payload?.orderBy || ""
		const orderDirection = payload?.orderDirection || "DESC"
		let query = `?limit=${limit}&page=${page}${filterQuery}${orderBy ? `&orderBy=${orderBy}&orderDirection=${orderDirection}` : ""}`
		if (payload?.postcode != null) query += `&postcode=${encodeURIComponent(payload.postcode)}`
		!payload && commit("SET_MAKERS_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/makers${query}`, config)
			.then((response) => {
				commit("SET_MAKERS", response.models)
				commit("SET_PAGES", response.paginate)
				commit("SET_MAKERS_LOADING", false)
			})
			.catch((error) => {
				commit("SET_MAKERS_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async getMakersList({ commit, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/list`, config)
			.then((response) => {
				commit("SET_MAKERS_LIST", response.models)
				commit("SET_MAKERS_LOADING", false)
			})
			.catch((error) => {
				commit("SET_MAKERS_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async getMakerById({ commit, dispatch }, id) {
		if (!id) {
			return null
		}
		commit("SET_MAKER_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/${id}`, config)
			.then((response) => {
				commit("SET_CHOSEN_MAKER", response)
				commit("SET_MAKER_LOADING", false)
			})
			.catch((error) => {
				commit("SET_MAKER_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async getDeliveryDays({ commit, dispatch }, id) {
		if (!id) {
			return null
		}

		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/public/makers/deliveryDays/${id}`,
			config
		)
			.then((response) => {
				commit("SET_MAKERS_DELIVERY_DAYS", response)
			})
			.catch((error) => {
				dispatch("handleError", { error })
			})
	},
	async createMaker({ state, commit, dispatch }) {
		commit("SET_MAKER_LOADING", true)
		const body = JSON.stringify({
			...state.newMaker,
			min_order_amount: state.newMaker.min_order_amount,
		})
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "POST",
			body,
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/makers`, config)
			.then((response) => {
				commit("SET_MAKER_LOADING", false)
				// Supply-side onboarding counter — today there is no metric for
				// "how many makers did we onboard this month".
				track(EVENTS.MAKER_SAVED, {
					operation: "created",
					maker_id: entityId(response?.id),
					maker_name: state.newMaker?.name,
					is_active: Boolean(state.newMaker?.is_active),
					min_order_amount_gbp: moneyGbp(state.newMaker?.min_order_amount),
				})
				navigateTo(`/makers/${response.id}`)
			})
			.catch((error) => {
				commit("SET_MAKER_LOADING", false)
				dispatch("handleError", { error, description: "create maker" })
			})
	},
	async updateMaker({ state, commit, dispatch }) {
		const body = JSON.stringify({
			...state.chosenMaker,
			min_order_amount: state.chosenMaker.min_order_amount,
		})
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		// The maker page binds `:loading="makerLoading"` on its Save buttons, but this
		// action never drove it — so the button stayed live during the PUT (double-submit
		// risk). Mirror getMakerById: raise the flag for the request, clear it in finally.
		commit("SET_MAKER_LOADING", true)
		// Resolves true on success, false on failure. The catch swallows the rejection
		// (every hub action does), so the caller cannot tell the two apart from the
		// promise alone — and the maker page has to, or it re-baselines its dirty-form
		// bar on a rejected save and tells the admin the change went through.
		return $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/${state.chosenMaker.id}`,
			config
		)
			.then(() => {
				track(EVENTS.MAKER_SAVED, {
					operation: "updated",
					maker_id: entityId(state.chosenMaker?.id),
					maker_name: state.chosenMaker?.name,
					is_active: Boolean(state.chosenMaker?.is_active),
					contract_signed: Boolean(state.chosenMaker?.contract_signed),
					min_order_amount_gbp: moneyGbp(state.chosenMaker?.min_order_amount),
					delivery_day_count: Array.isArray(state.chosenMaker?.deliveryDays)
						? state.chosenMaker.deliveryDays.length
						: undefined,
				})
				commit("SET_ATTACHMENT_ERROR", false)
				commit("SET_SUCCESS_MESSAGE", "Successfully updated Maker")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 5000)
				return true
			})
			.catch((error) => {
				// `handleError` relays the backend's plain-text body, and it commits AFTER
				// whatever we commit here — so the two hardcoded strings this used to set
				// were always overwritten and never reached a screen. Dispatch first, then
				// override only the one case where the body is useless.
				dispatch("handleError", { error, description: "updated maker" })

				// Only the contract check answers `res.sendStatus(400)`, whose body is the
				// bare "Bad Request". Every other 400 here carries something actionable —
				// over-long pickup instructions, a non-maker tag — as a plain string or as
				// the `{errors:{field}}` / `{message}` JSON handleError already unpacks.
				// Coercing a non-string body to "" collapsed those two cases, so any JSON
				// 400 lost its real message to the contract remedy. Test the body's shape.
				const status = error?.response?.status ?? error?.status
				const data = error?.data
				const isBareBadRequest =
					data === undefined ||
					data === null ||
					(typeof data === "string" && ["", "Bad Request"].includes(data.trim()))
				if (status === 400 && isBareBadRequest) {
					commit("SET_ERROR_MESSAGE", "Please sign the contract to be activated.")
				}
				return false
			})
			.finally(() => {
				commit("SET_MAKER_LOADING", false)
			})
	},
	async saveProductCategoryOrder({ state, commit, dispatch }, data) {
		const body = JSON.stringify(data)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/productCategories/updateProductCategorySort`,
			config
		).catch((error) => {
			dispatch("handleError", { error, description: "updated" })
		})
	},
	async saveMakerCategoryOrder({ state, commit, dispatch }, data) {
		const body = JSON.stringify(data)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/makerCategories/updateMakerCategorySort`,
			config
		).catch((error) => {
			dispatch("handleError", { error, description: "updated" })
		})
	},
	async getProductCategories({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const page = payload?.page || 1
		const limit = payload?.limit || 20
		const orderBy = payload?.orderBy || ""
		const orderDirection = payload?.orderDirection || "DESC"
		const query = `?limit=${limit}&page=${page}${filterQuery}${orderBy ? `&orderBy=${orderBy}&orderDirection=${orderDirection}` : ""}`

		if (!payload) commit("SET_PRODUCT_CATEGORIES_LOADING", true)

		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/productCategories${query}`, config)
			.then((response) => {
				commit("SET_PRODUCT_CATEGORIES", response.productCategories)
				commit("SET_PAGES", response.paginate)
				commit("SET_PRODUCT_CATEGORIES_LOADING", false)
			})
			.catch((error) => {
				commit("SET_PRODUCT_CATEGORIES_LOADING", false)

				dispatch("handleError", { error })
			})
	},
	async getProductCategoriesList({ commit, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/productCategories/list`, config)
			.then((response) => {
				commit("SET_PRODUCT_CATEGORIES_LIST", response.productCategories)
				commit("SET_PRODUCT_CATEGORIES_LOADING", false)
			})
			.catch((error) => {
				commit("SET_PRODUCT_CATEGORIES_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async getProductCategoriesByMaker({ commit, dispatch }, payload) {
		commit("SET_PRODUCT_CATEGORIES_LOADING", true)
		const normalizedPayload =
			typeof payload === "object" && payload !== null ? payload : { id: payload }
		const { id, orderBy = "", orderDirection = "DESC", filters = [] } = normalizedPayload
		const name = filters.find((f: string[]) => f[0] === "name")?.[1] ?? ""

		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/productCategories/search?maker_id=${id}${name ? `&name=${name}` : ""}${orderBy ? `&orderBy=${orderBy}&orderDirection=${orderDirection}` : ""}`,
			config
		)
			.then((response) => {
				commit("SET_PRODUCT_CATEGORIES", response.productCategories)
				commit("SET_PAGES", false)
				commit("SET_PRODUCT_CATEGORIES_LOADING", false)
			})
			.catch((error) => {
				commit("SET_PRODUCT_CATEGORIES_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async getProductCategoryById({ commit, dispatch }, id) {
		commit("SET_PRODUCT_CATEGORIES_LOADING", true)

		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/productCategories/${id}`, config)
			.then((response) => {
				commit("SET_CHOSEN_PRODUCT_CATEGORY", response)
				commit("SET_PRODUCT_CATEGORIES_LOADING", false)
			})
			.catch((error) => {
				commit("SET_PRODUCT_CATEGORIES_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async updateProductCategory({ commit, state, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		const body = JSON.stringify(state.chosenProductCategory)

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/productCategories/${
				state.chosenProductCategory.id
			}`,
			{
				method: "PUT",
				body,
				...config,
			}
		)
			.then(() => {
				track(EVENTS.CATALOGUE_ENTITY_SAVED, {
					entity_type: "product_category",
					operation: "updated",
				})
				commit("SET_SUCCESS_MESSAGE", "Successfully updated product category")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 5000)
			})
			.catch((error) => {
				dispatch("handleError", {
					error,
					description: "updated product category",
				})
			})
	},
	async createProductCategory({ commit, dispatch }, payload) {
		commit("SET_PRODUCT_CATEGORIES_LOADING", true)

		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		const body = JSON.stringify({
			...payload.body,
			maker_id: payload.body.maker_id.id,
		})

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/productCategories`, {
			method: "POST",
			body,
			...config,
		})
			.then((response) => {
				track(EVENTS.CATALOGUE_ENTITY_SAVED, {
					entity_type: "product_category",
					operation: "created",
				})
				commit("SET_PRODUCT_CATEGORIES", response.productCategories)
				commit("SET_PRODUCT_CATEGORIES_LOADING", false)
				payload.redirect && navigateTo(`/product-categories`)
			})
			.catch((error) => {
				commit("SET_PRODUCT_CATEGORIES_LOADING", false)
				dispatch("handleError", {
					error,
					description: "create product category",
				})
			})
	},
	async syncWithHubspot({ commit, dispatch }) {
		commit("SET_SYNC_HUBSPOT_LOADING", true)

		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "GET",
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/makers/syncWithHubspot`, config)
			.then((response) => {
				// Manual-sync frequency is the business case for automating it.
				track(EVENTS.HUBSPOT_SYNC_RUN, { sync_result: "success" })
				commit("SET_SYNC_HUBSPOT_LOADING", false)
				commit("SET_SUCCESS_MESSAGE", "Successful synchronization with HubSpot.")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 3000)
			})
			.catch((error) => {
				commit("SET_SYNC_HUBSPOT_LOADING", false)
				dispatch("handleError", { error, description: "Nothing to migrate" })
			})
	},

	async saveAttachment({ commit, dispatch }, payload) {
		commit("SET_ATTACHMENT_ERROR", false)
		commit("SET_ATTACHMENT_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
			},
			method: "POST",
			body: payload.body,
		}

		try {
			// Returns the created attachment rows (with ids), e.g. for pairing a
			// mobile banner image to its desktop banner. Existing callers ignore it.
			const response = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/attachments/${
					payload.model
				}/${payload.type}/${payload.id}`,
				config
			)
			commit("SET_ATTACHMENT_ERROR", false)
			commit("SET_ATTACHMENT_LOADING", false)
			track(EVENTS.ATTACHMENT_CHANGED, {
				change_type: "uploaded",
				model: enumValue(payload.model),
				attachment_type: enumValue(payload.type),
				entity_id: entityId(payload.id),
				file_count: Array.isArray(response) ? response.length : undefined,
			})
			return response
		} catch (error) {
			commit("SET_ATTACHMENT_LOADING", false)
			commit("SET_ATTACHMENT_ERROR", error.message)
			dispatch("handleError", { error })
		}
	},
	async deleteAttachment({ commit, dispatch }, payload) {
		commit("SET_ATTACHMENT_ERROR", false)
		commit("SET_ATTACHMENT_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-Type": "multipart/form-data",
			},
			method: "DELETE",
			body: payload.body,
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/attachments/${payload.id}`, config)
			.then(() => {
				track(EVENTS.ATTACHMENT_CHANGED, {
					change_type: "deleted",
					entity_id: entityId(payload.id),
				})
				commit("SET_ATTACHMENT_ERROR", false)
				commit("SET_ATTACHMENT_LOADING", false)
			})
			.catch((error) => {
				commit("SET_ATTACHMENT_ERROR", error.message)
				commit("SET_ATTACHMENT_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async changeEntityStatus({ commit, dispatch }, payload) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		const body = JSON.stringify(payload)

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/${payload.entity}/changeStatus/${payload.id}`,
			{ method: "PUT", body, ...config }
		)
			.then(() => {
				// This one endpoint backs both the order status dropdown and the
				// activate/deactivate toggle on nine list pages. Split them: order
				// lifecycle timings and catalogue deactivations are different
				// questions and belong in different events.
				if (payload.entity === "orders") {
					track(EVENTS.ORDER_STATUS_CHANGED, {
						order_id: entityId(payload.id),
						field: payload.field,
						set_value: enumValue(payload.setValue),
						page_route: useRoute()?.name,
					})
				} else {
					track(EVENTS.ENTITY_STATUS_TOGGLED, {
						// entityType(), not enumValue(): payload.entity is the camelCase
						// route segment ("productCategories"), which enumValue only
						// lowercased into "productcategories" — a second spelling of the
						// "product_category" every other event here emits.
						entity_type: entityType(payload.entity),
						entity_id: entityId(payload.id),
						field: payload.field,
						set_value: payload.setValue,
						page_route: useRoute()?.name,
					})
				}
				commit("SET_ENTITY_STATE", payload)
				commit("SET_SUCCESS_MESSAGE", "Successfully updated")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 2000)
			})
			.catch((error) => {
				dispatch("handleError", { error, description: "updated" })
			})
	},
	async fetchBulkAttachments({ commit }, { modelName, ids, collections }) {
		// The backend caps each request at 200 ids (attachmentController MAX_IDS —
		// a deliberate DoS/heap guardrail). Every caller today is paginated well
		// under that, but chunk defensively so this helper stays safe if reused for
		// an unpaginated list.
		const CHUNK_SIZE = 200
		const uniqueIds = [...new Set(ids as (string | number)[])]
		if (uniqueIds.length === 0 || !collections?.length) return {}

		const url = `${import.meta.env.VITE_AND_DINE_API}/api/public/attachments/${modelName}?collections=${encodeURIComponent(JSON.stringify(collections))}`

		const chunks: (string | number)[][] = []
		for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
			chunks.push(uniqueIds.slice(i, i + CHUNK_SIZE))
		}

		// One request per ≤200-id chunk; chunks are disjoint so the maps merge
		// without collision. Each chunk falls back to {} so one bad batch only
		// drops its own images.
		const results = await Promise.all(
			chunks.map(async (chunk) => {
				try {
					const attRes = await $fetch(url, {
						method: "POST",
						headers: { "content-type": "application/json" },
						body: JSON.stringify({ ids: chunk }),
					})
					return (attRes as any)?.data || {}
				} catch (error) {
					console.error(`Failed to fetch ${modelName} attachments:`, error)
					return {} // Non-fatal: entities still load without images
				}
			})
		)

		return Object.assign({}, ...results)
	},
	getPackages: debounce(async function ({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const page = payload?.page || 1
		const limit = payload?.limit || 20
		const orderBy = payload?.orderBy || ""
		const orderDirection = payload?.orderDirection || "DESC"
		const query = `?limit=${limit}&page=${page}${filterQuery}${orderBy ? `&orderBy=${orderBy}&orderDirection=${orderDirection}` : ""}`
		!payload && commit("SET_PACKAGES_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/packages${query}`, config)
			.then(async (response) => {
				let packages = response.packages || []
				if (packages.length > 0) {
					const ids = packages.map((p: any) => p.id)
					const byId = await dispatch("fetchBulkAttachments", {
						modelName: "package",
						ids,
						collections: ["mainImage"],
					})
					packages = packages.map((p: any) => ({
						...p,
						attachments: { ...(p.attachments || {}), mainImage: byId[p.id]?.mainImage ?? [] },
					}))
				}
				commit("SET_PACKAGES", packages)
				commit("SET_PAGES", response.paginate)
				commit("SET_PACKAGES_LOADING", false)
			})
			.catch((error) => {
				commit("SET_PACKAGES_LOADING", false)
				dispatch("handleError", { error })
			})
	}, 200),
	async getPackageById({ commit, dispatch }, id) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/packages/${id}`, config)
			.then((response) => {
				commit("SET_CHOSEN_PACKAGE", response)
				commit("SET_PACKAGES_LOADING", false)
			})
			.catch((error) => {
				commit("SET_PACKAGES_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async getPackagesByMaker({ commit, state }, payload) {
		commit("SET_PACKAGES_LOADING", true)
		const orderBy = payload?.orderBy || ""
		const orderDirection = payload?.orderDirection || "DESC"
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		if (state.user.makers?.length > 0)
			await $fetch(
				`${
					import.meta.env.VITE_AND_DINE_API
				}/api/users/packages/search?maker_id=${state.chosenMaker.id}${orderBy ? `&orderBy=${orderBy}&orderDirection=${orderDirection}` : ""}`,
				config
			)
				.then((response) => {
					commit("SET_PACKAGES", response.packages)
					commit("SET_PACKAGES_LOADING", false)
				})
				.catch((error) => {
					commit("SET_PACKAGES_LOADING", false)
				})
	},
	async updatePackage({ state, commit, dispatch }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		const body = JSON.stringify({
			...state.chosenPackage,
			maker_id: state.chosenPackage.maker_id,
		})

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/packages/${state.chosenPackage.id}`,
			{ method: "PUT", body, ...config }
		)
			.then(() => {
				track(EVENTS.CATALOGUE_ENTITY_SAVED, {
					entity_type: "package",
					operation: "updated",
				})
				commit("SET_SUCCESS_MESSAGE", "Successfully updated package")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 5000)
			})
			.catch((error) => {
				dispatch("handleError", { error, description: "updated package" })
			})
	},
	async createPackage({ commit, dispatch }, payload) {
		commit("SET_PACKAGES_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		const body = JSON.stringify(payload.body)

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/packages`, {
			method: "POST",
			body,
			...config,
		})
			.then((response) => {
				commit("SET_PACKAGES_LOADING", false)
				track(EVENTS.CATALOGUE_ENTITY_SAVED, {
					entity_type: "package",
					operation: "created",
					entity_id: entityId(response?.id),
				})
				commit("SET_SUCCESS_MESSAGE", "Successfully created packages")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 5000)
				navigateTo(`/packages/`)
			})
			.catch((error) => {
				commit("SET_PACKAGES_LOADING", false)
				dispatch("handleError", { error, description: "create package" })
			})
	},
	async getOrganisations({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const page = payload?.page || 1
		const limit = payload?.limit || 20
		const orderBy = payload?.orderBy || ""
		const orderDirection = payload?.orderDirection || "DESC"
		const query = `?limit=${limit}&page=${page}${filterQuery}${orderBy ? `&orderBy=${orderBy}&orderDirection=${orderDirection}` : ""}`
		!payload && commit("SET_ORGANISATIONS_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations${query}`, config)
			.then((response) => {
				commit("SET_ORGANISATIONS", response.organisations)
				commit("SET_PAGES", response.paginate)
				commit("SET_ORGANISATIONS_LOADING", false)
			})
			.catch((error) => {
				commit("SET_ORGANISATIONS_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async getOrganisation({ commit, dispatch }, payload) {
		!payload && commit("SET_ORGANISATIONS_LOADING", true)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload}`, config)
			.then((response) => {
				commit("SET_CHOSEN_ORGANISATION", response)
				commit("SET_ORGANISATIONS_LOADING", false)
			})
			.catch((error) => {
				commit("SET_ORGANISATIONS_LOADING", false)
				dispatch("handleError", { error })
			})
	},
	async addOrgMember({ commit, dispatch }, payload) {
		const body = JSON.stringify({
			email: payload.email,
			first_name: payload.first_name,
			last_name: payload.last_name,
			auto_activate: payload.auto_activate,
		})
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		await $fetch(
			`${
				import.meta.env.VITE_AND_DINE_API
			}/api/users/organisations/addCustomer/${payload.organisationId}`,
			config
		)
			.then(async () => {
				// B2B seat expansion — the account-growth metric. skipRefresh is the
				// bulk CSV import path, so it must be tracked before the early return.
				track(EVENTS.ORGANISATION_MEMBERSHIP_CHANGED, {
					change_type: "member_added",
					organisation_id: entityId(payload?.organisationId),
					auto_activate: Boolean(payload?.auto_activate),
					is_bulk_import: Boolean(payload?.skipRefresh),
				})
				if (payload?.skipRefresh) {
					commit("SET_ORGANISATION_ERROR", "")
					return
				}
				const organisation = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}`,
					{
						headers: config.headers,
					}
				)
				commit("SET_CHOSEN_ORGANISATION", organisation)
				commit("SET_ORGANISATION_ERROR", "")
			})
			.catch((error) => {
				const errorMessage =
					error.response?._data?.message ||
					"This email is already associated with this organisation."
				commit("SET_ORGANISATION_ERROR", errorMessage)
				Sentry.captureException(error)
				dispatch("handleError", { error, description: "add org member" })
				if (payload.skipRefresh) throw error
			})
	},
	async removeOrgMember({ commit, dispatch }, payload) {
		const body = JSON.stringify({ email: payload.email })
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		await $fetch(
			`${
				import.meta.env.VITE_AND_DINE_API
			}/api/users/organisations/removeCustomer/${payload.organisationId}`,
			config
		)
			.then(async () => {
				const organisation = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}`,
					{
						headers: config.headers,
					}
				)

				track(EVENTS.ORGANISATION_MEMBERSHIP_CHANGED, {
					change_type: "member_removed",
					organisation_id: entityId(payload?.organisationId),
					member_count_after: Array.isArray(organisation?.customers)
						? organisation.customers.length
						: undefined,
				})
				commit("SET_CHOSEN_ORGANISATION", organisation)
			})
			.catch((error) => {
				Sentry.captureException(error)
				dispatch("handleError", { error, description: "remove org member" })
			})
	},
	async resendOrgInvite({ state, commit, dispatch }, payload) {
		const body = JSON.stringify({ pendingOrgCustomerId: payload.pendingOrgCustomerId })
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		commit("SET_ORGANISATION_ERROR", "")

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/resendInvite`,
			config
		)
			.then(() => {
				track(EVENTS.ORGANISATION_MEMBERSHIP_CHANGED, {
					// The only caller sends { pendingOrgCustomerId } and nothing else
					// (pages/organisations/[id].vue:1171), so reading an organisation id
					// off the payload left every invite_resent unattributed. The page
					// that owns this button is the chosen org's, so take it from state.
					change_type: "invite_resent",
					organisation_id: entityId(state?.chosenOrg?.id),
					pending_org_customer_id: entityId(payload?.pendingOrgCustomerId),
				})
				commit("SET_SUCCESS_MESSAGE", "Invite resent")
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false)
				}, 5000)
			})
			.catch((error) => {
				const data = error.response?._data
				const errorMessage =
					(typeof data === "string" ? data : data?.error) ||
					"Failed to resend invite. Please try again."
				commit("SET_ORGANISATION_ERROR", errorMessage)
				Sentry.captureException(error)
				dispatch("handleError", { error, description: "resend org invite" })
				throw error
			})
	},
	updateOrganisation({ commit, dispatch }, payload) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		return $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}`,
			{ method: "PUT", body: payload, ...config }
		)
			.then(() => {
				return $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}`,
					config
				).then((response) => {
					// A mis-set service_fee_rate silently mis-bills every order for
					// this org, which is why this surface is worth watching.
					// Profile fields only. service_fee_rate and subscription_type_id live
					// on the NESTED response.subscription, and this GET runs BEFORE the
					// updateSubscription dispatch below — so reading them here gave
					// undefined at best and the pre-save values at worst, on an event
					// emitted before the subscription write could fail. They are tracked
					// in updateSubscription instead, once they are real.
					track(EVENTS.ORGANISATION_SAVED, {
						operation: "updated",
						section: "profile",
						organisation_id: entityId(response?.id),
					})
					commit("SET_CHOSEN_ORGANISATION", response)
					// updateSubscription already reports its own errors (handleError +
					// Sentry) — swallow its rethrow here so a subscription-only failure
					// doesn't double-report, or read as the organisation PUT itself
					// having failed (which would wrongly block the caller's dependent
					// address save below).
					return dispatch("updateSubscription", payload).catch(() => {})
				})
			})
			.catch((error) => {
				Sentry.captureException(error)
				dispatch("handleError", { error, description: "updated organisation" })
				throw error
			})
	},
	async getOrganisationInvoiceConfig({ commit, dispatch }, organisationId) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		try {
			const response = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${organisationId}/invoiceConfiguration`,
				config
			)
			commit("SET_CHOSEN_ORG_INVOICE_CONFIG", response)
		} catch (error) {
			Sentry.captureException(error)
			dispatch("handleError", { error, description: "fetched invoice configuration" })
		}
	},
	async updateOrganisationInvoiceConfig({ commit, dispatch }, payload) {
		const { organisationId, ...body } = payload
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		try {
			const response = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${organisationId}/invoiceConfiguration`,
				{ method: "PUT", body, ...config }
			)
			commit("SET_CHOSEN_ORG_INVOICE_CONFIG", response)
			track(EVENTS.ORGANISATION_SAVED, {
				operation: "updated",
				section: "invoice_config",
				organisation_id: entityId(payload?.organisationId ?? payload?.organisation_id),
			})
			commit("SET_SUCCESS_MESSAGE", "Invoice configuration saved")
			setTimeout(() => {
				commit("SET_SUCCESS_MESSAGE", false)
			}, 5000)
		} catch (error) {
			Sentry.captureException(error)
			dispatch("handleError", { error, description: "updated invoice configuration" })
			throw error
		}
	},
	async createOrganisation({ state, commit, dispatch }) {
		const headers = {
			accept: "application/json",
			"x-access-token-user": useCookie("auth_token").value ?? "",
			"content-type": "application/json",
		}

		try {
			const org = await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations`, {
				method: "POST",
				headers,
				body: {
					...state.newOrganisation,
					is_active: true,
					is_creditable: false,
					credit_limit: 0,
				},
			})
			track(EVENTS.ORGANISATION_SAVED, {
				operation: "created",
				section: "profile",
				organisation_id: entityId(org?.id),
			})
			const subscription = {
				subscription_type_id: 2,
				service_fee_rate: 0,
				min_service_fee_amount: 0,
				delivery_net: 0,
				delivery_address: { line_1: "", line_2: "", city: "", zip: "" },
				days: [
					{
						day: "Monday",
						time: "12:00",
						config_type: "",
						maker_count: "",
						order_cutoff: "0",
					},
					{
						day: "Tuesday",
						time: "12:00",
						config_type: "",
						maker_count: "",
						order_cutoff: "0",
					},
					{
						day: "Wednesday",
						time: "12:00",
						config_type: "",
						maker_count: "",
						order_cutoff: "0",
					},
					{
						day: "Thursday",
						time: "12:00",
						config_type: "",
						maker_count: "",
						order_cutoff: "0",
					},

					{
						day: "Monday",
						time: "12:00",
						config_type: "",
						maker_count: "",
						order_cutoff: "0",
					},
					{
						day: "Friday",
						time: "12:00",
						config_type: "",
						maker_count: "",
						order_cutoff: "0",
					},
				],
			}

			const subConfigs = []

			await dispatch("updateSubscription", {
				organisationId: org.id,
				subscription,
				subConfigs,
			})

			return org
		} catch (error) {
			dispatch("handleError", { error, description: "create organisation" })
			Sentry.captureException(error)
			throw error
		}
	},
	async updateCustomerRole({ commit, dispatch }, payload) {
		const body = JSON.stringify(payload)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${
				payload.organisationId
			}/updateRole`,
			config
		)
			.then(async () => {
				const organisation = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}`,
					{
						headers: config.headers,
					}
				)

				track(EVENTS.ORGANISATION_MEMBERSHIP_CHANGED, {
					change_type: "role_changed",
					organisation_id: entityId(payload?.organisationId),
					new_role: enumValue(payload?.customerRole ?? payload?.role),
				})
				commit("SET_CHOSEN_ORGANISATION", organisation)
			})
			.catch((error) => {
				Sentry.captureException(error)
				dispatch("handleError", { error, description: "updated customer role" })
			})
	},
	async createTeam({ commit, dispatch }, payload) {
		const body = JSON.stringify(payload)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "POST",
			body,
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${
				payload.organisationId
			}/organisationTeams`,
			config
		)
			.then(async () => {
				track(EVENTS.TEAM_CHANGED, {
					change_type: "created",
					organisation_id: entityId(payload?.organisationId),
					team_id: entityId(payload?.teamId ?? payload?.id),
				})
				commit("SET_CREATE_TEAM_ERROR", false)

				const organisation = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}`,
					{
						headers: config.headers,
					}
				)

				commit("SET_CHOSEN_ORGANISATION", organisation)
			})
			.catch((error) => {
				commit(
					"SET_CREATE_TEAM_ERROR",
					"There was an error creating your team. If this error persists please reach out to support."
				)
				dispatch("handleError", { error })
				Sentry.captureException(error)
			})
	},
	async updateTeam({ commit, dispatch }, payload) {
		const body = JSON.stringify(payload)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${
				payload.organisationId
			}/organisationTeams/${payload.teamId}`,
			config
		)
			.then(async () => {
				track(EVENTS.TEAM_CHANGED, {
					change_type: "updated",
					organisation_id: entityId(payload?.organisationId),
					team_id: entityId(payload?.teamId ?? payload?.id),
				})
				commit("SET_UPDATE_TEAM_ERROR", false)

				const organisation = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}`,
					{
						headers: config.headers,
					}
				)

				commit("SET_CHOSEN_ORGANISATION", organisation)
			})
			.catch((error) => {
				commit(
					"SET_UPDATE_TEAM_ERROR",
					"There was an error updating your team. If this error persists please reach out to support."
				)
				Sentry.captureException(error)
				dispatch("handleError", { error })
			})
	},
	async removeCustomerFromTeam({ commit, dispatch }, payload) {
		const body = JSON.stringify(payload)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}
		commit("SET_TEAM_LOADING", true)

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${
				payload.organisationId
			}/organisationTeams/${payload.teamId}/removeCustomer`,
			config
		)
			.then(async () => {
				track(EVENTS.TEAM_CHANGED, {
					change_type: "member_removed",
					organisation_id: entityId(payload?.organisationId),
					team_id: entityId(payload?.teamId ?? payload?.id),
				})
				commit("SET_REMOVE_FROM_TEAM_ERROR", false)

				const organisation = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}`,
					{
						headers: config.headers,
					}
				)

				commit("SET_TEAM_LOADING", false)
				commit("SET_CHOSEN_ORGANISATION", organisation)
			})
			.catch((error) => {
				commit("SET_TEAM_LOADING", false)
				commit(
					"SET_REMOVE_FROM_TEAM_ERROR",
					"There was an error removing this team member. If this error persists please reach out to support."
				)
				Sentry.captureException(error)
				dispatch("handleError", { error })
			})
	},
	async resendMakerContract({ commit, state, dispatch }, payload) {
		const body = JSON.stringify(payload)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "POST",
			body,
		}
		commit("SET_RESEND_CONTRACT_LOADING", true)

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/${
				state.chosenMaker.id
			}/resendContract`,
			config
		)
			.then(() => {
				// >1 resend per maker means the e-sign flow is broken.
				track(EVENTS.MAKER_CONTRACT_RESENT, {
					// Same maker the request targets (the URL uses state.chosenMaker.id).
					// The detail page sends only the resend form fields, so reading the
					// id from payload left this undefined.
					maker_id: entityId(state.chosenMaker?.id ?? payload?.makerId ?? payload?.id),
				})
				commit("SET_RESEND_CONTRACT_ERROR", false)
				commit("SET_RESEND_CONTRACT_LOADING", false)
			})
			.catch((error) => {
				commit("SET_RESEND_CONTRACT_LOADING", false)
				commit("SET_RESEND_CONTRACT_ERROR", error)
				Sentry.captureException(error)
				dispatch("handleError", { error })
			})
	},
	// Admin-only manual override now that maker contracts are signed outside the platform.
	// `makerId` is required, not defaulted off chosenMaker — this is called from the maker detail
	// page AND from the row toggle on the makers list, where there is no chosen maker.
	// Rethrows so the caller can surface the failure and leave the record's real state on screen.
	// `signedAt` is the date the contract was signed (`YYYY-MM-DD`), collected by the date modal.
	// Only sent when marking as signed and only when supplied — the backend defaults to today, and
	// it is meaningless when clearing the flag.
	async updateMakerContractSigned(
		{ commit, state, dispatch },
		{ makerId, signed, signedAt }: { makerId: string; signed: boolean; signedAt?: string | null }
	) {
		try {
			const payload: { signed: boolean; signed_at?: string } = { signed }
			if (signed && signedAt) payload.signed_at = signedAt

			const response = await $fetch<{
				MakerContracts: { id: number; contract_id: string; signed_at: string | null }[]
			}>(`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/${makerId}/contract`, {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "PUT",
				body: JSON.stringify(payload),
			})

			// Write the backend's records back to whichever slice holds this maker. Both are
			// no-ops when the maker isn't loaded there, so the detail page and the list page can
			// share one action without either knowing about the other.
			if (state.chosenMaker?.id === makerId) {
				commit("SET_CHOSEN_MAKER_CONTRACTS", response.MakerContracts)
			}
			commit("SET_MAKER_CONTRACTS_IN_LIST", { makerId, MakerContracts: response.MakerContracts })
		} catch (error) {
			Sentry.captureException(error)
			dispatch("handleError", { error })
			throw error
		}
	},
	async downloadMakerReport({ commit, dispatch }, payload) {
		commit("SET_MAKER_REPORT_LOADING", true)

		const config = {
			headers: {
				accept: "application/pdf",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/octet-stream; charset=utf-8",
			},
		}

		await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/makerReport?from=${
				payload.from
			}&to=${payload.to}&maker_id=${payload.maker_id}`,
			config
		)
			.then((response) => {
				commit("SET_MAKER_REPORT", response)
				commit("SET_MAKER_REPORT_LOADING", false)
			})
			.catch((error) => {
				commit("SET_MAKER_REPORT_LOADING", false)
				commit("SET_MAKER_REPORT", false)
				dispatch("handleError", { error, description: "download maker report" })
			})
	},

	async fetchGeneratedInvoices({ dispatch, commit }) {
		try {
			const config = {
				method: "GET",
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
				},
			}
			const response = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/getUnsentMakerInvoicesEmailStatuses`,
				config
			)
			commit("SET_GENERATED_INVOICES", response.items)
		} catch (error) {
			Sentry.captureException(error)
			dispatch("handleError", {
				error,
				description: "fetch generated invoices",
			})
		}
	},
	async sendMakerInvoiceEmailsAction({ dispatch, commit }) {
		try {
			const config = {
				method: "POST",
				headers: {
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"Content-Type": "application/json",
				},
				body: JSON.stringify({}),
			}

			const response = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/sendMakerInvoiceEmails`,
				config
			)

			// The endpoint returns { prepared, skipped, failed } arrays — or a plain
			// string ("No invoices to send") when there is nothing to do. There is no
			// `processed` field, so reading one made this event land with no
			// properties at all.
			const emailResults = typeof response === "string" ? null : response
			track(EVENTS.MAKER_INVOICE_EMAILS_SENT, {
				prepared_count: Array.isArray(emailResults?.prepared) ? emailResults.prepared.length : 0,
				skipped_count: Array.isArray(emailResults?.skipped) ? emailResults.skipped.length : 0,
				failed_count: Array.isArray(emailResults?.failed) ? emailResults.failed.length : 0,
				had_nothing_to_send: emailResults === null,
			})
			commit("SET_SUCCESS_MESSAGE", response.message || "Emails processed.")
			setTimeout(() => {
				commit("SET_SUCCESS_MESSAGE", false)
			}, 4000)
		} catch (error) {
			Sentry.captureException(error)
			dispatch("handleError", {
				error,
				description: "send maker invoice emails",
			})
		}
	},
	async regenerateInvoice({ commit, dispatch }, payload) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		commit("SET_REGENERATE_INVOICE_LOADING", true)
		try {
			await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/invoice/${payload.orderId}`,
				{ method: "PUT", ...config }
			)

			commit("SET_REGENERATE_INVOICE_ERROR", false)
			commit("SET_REGENERATE_INVOICE_LOADING", false)
		} catch (error) {
			commit("SET_REGENERATE_INVOICE_LOADING", false)
			commit("SET_REGENERATE_INVOICE_ERROR", error)
			Sentry.captureException(error)
			dispatch("handleError", { error, description: "regenerate invoice" })
		}
	},

	async updateSubscription({ commit, dispatch }, payload) {
		const headers = {
			accept: "application/json",
			"x-access-token-user": useCookie("auth_token").value ?? "",
			"content-type": "application/json",
		}
		commit("SET_SUBSCRIPTION_LOADING", true)
		try {
			await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}/subscription`,
				{ method: "PUT", headers, body: JSON.stringify(payload.subscription) }
			)

			const organisation = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}`,
				{ headers }
			)
			commit("SET_CHOSEN_ORGANISATION", organisation)

			// Emitted here, not from updateOrganisation: this is the first point the
			// subscription PUT has succeeded AND been read back. A mis-set
			// service_fee_rate silently mis-bills every order for this org, which is
			// why this surface is worth watching.
			track(EVENTS.ORGANISATION_SAVED, {
				operation: "updated",
				section: "subscription",
				organisation_id: entityId(payload?.organisationId),
				service_fee_rate: numeric((organisation as any)?.subscription?.service_fee_rate),
				subscription_type_id: entityId((organisation as any)?.subscription?.subscription_type_id),
				sub_config_count: Array.isArray(payload?.subConfigs) ? payload.subConfigs.length : 0,
			})

			return await dispatch("updateSubConfig", {
				organisationId: payload.organisationId,
				subConfigs: payload.subConfigs || [],
			})
		} catch (error) {
			dispatch("handleError", { error, description: "updated subscription" })
			Sentry.captureException(error)
			throw error
		} finally {
			commit("SET_SUBSCRIPTION_LOADING", false)
		}
	},
	async getAuditLog({ dispatch }, { entityType, entityId }) {
		try {
			return await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/auditLog/${entityType}/${entityId}?limit=9999`,
				{
					headers: {
						accept: "application/json",
						"x-access-token-user": useCookie("auth_token").value ?? "",
					},
				}
			)
		} catch (error) {
			dispatch("handleError", { error })
		}
	},
	async getActorLabel(_, { id, type }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		const endpoint =
			type === "customer"
				? `${import.meta.env.VITE_AND_DINE_API}/api/users/customers/${id}`
				: `${import.meta.env.VITE_AND_DINE_API}/api/users/${id}`
		try {
			const response = await $fetch(endpoint, config)
			const label =
				`${response.first_name ?? ""} ${response.last_name ?? ""}`.trim() ||
				response.email ||
				"Unknown"
			return { id, type, label }
		} catch {
			return { id, type, label: "Unknown" }
		}
	},
	handleError({ commit }, { error, description }) {
		const actionPart = description ? `to ${description} ` : ""
		const defaultMessage = "Failed " + actionPart + `- ${error.statusText || "An Error occurred"}`
		let errorMessage = defaultMessage

		if (
			error.response &&
			(error.response.status === 400 ||
				error.response.status === 409 ||
				error.response.status === 500)
		) {
			const errorData = error.data
			if (errorData) {
				// Handle nested errors object (e.g., {errors: {note: "message"}})
				if (errorData.errors && typeof errorData.errors === "object") {
					const firstErrorKey = Object.keys(errorData.errors)[0]
					errorMessage = firstErrorKey ? errorData.errors[firstErrorKey] : defaultMessage
				} else if (typeof errorData === "string") {
					errorMessage = errorData
				} else if (errorData.message) {
					errorMessage = errorData.message
				} else {
					errorMessage = defaultMessage
				}
			}
		}

		// "Internal Server Error" is what a 500 body says verbatim — useless to
		// the user. Replace with a friendlier message; keep specific 400s
		// (validation messages) untouched because those ARE actionable.
		if (error.response?.status === 500 || errorMessage === "Internal Server Error") {
			errorMessage =
				"Something went wrong. Please try again or contact support if this keeps happening."
		}

		commit("SET_ERROR_MESSAGE", errorMessage)

		// The single error channel for the hub. Emitted from here rather than from
		// subscribeAction's `error` hook, because almost every action in this file
		// ends `.catch(... dispatch("handleError") ...)` without rethrowing — the
		// action resolves, so that hook never fires.
		// Tells Tier 3 that whichever write is in flight actually failed — these
		// actions swallow their error and resolve, so `after` alone can't tell.
		noteErrorShown()

		const isAuthError = error.response?.status === 401 || error.response?.status === 403
		track(EVENTS.APP_ERROR_SHOWN, {
			// A small, stable slug set ("create order", "cancel order", ...) — the
			// only endpoint proxy available, since handleError gets no request URL.
			error_description: enumValue(description),
			http_status: numeric(error.response?.status),
			// Deliberately NOT the raw message. handleError builds it from arbitrary
			// backend validation copy, which echoes user input — names, phone
			// numbers, addresses, order text. Scrubbing emails only covered one
			// class of that, and the surface is unbounded, so nothing user-derived
			// goes to Mixpanel. error_description (our own slug) + http_status are
			// what the error-hotspot report groups on anyway; the message itself is
			// in Sentry, which is the right place for it.
			error_message_length: numeric(String(errorMessage ?? "").length),
			page_route: useRoute()?.name,
			is_auth_error: isAuthError,
		})

		// Auth errors → auto-dismiss + redirect to login.
		// Other errors → auto-dismiss after a generous window (15s) so users
		// have time to read but the banner doesn't linger forever.
		if (error.response && (error.response.status === 401 || error.response.status === 403)) {
			setTimeout(() => {
				commit("SET_ERROR_MESSAGE", false)
				navigateTo("/login")
			}, 2500)
		} else {
			setTimeout(() => commit("SET_ERROR_MESSAGE", false), 15000)
		}
	},
	async getSubscriptionConfigurations({ commit, dispatch }, payload) {
		const filters = payload?.filters || []
		const filterQuery = generateFilterQuery(filters)
		const page = payload?.page || 1
		const limit = payload?.limit || 20
		const query = `?limit=${limit}&page=${page}${filterQuery}`
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		try {
			const response = await $fetch<{
				subscriptionConfigurations: any[]
				paginate: {
					limit: number
					offset: number
					order: string[][]
					total: number
				}
			}>(`${import.meta.env.VITE_AND_DINE_API}/api/users/subConfig${query}`, config)

			commit("SET_SUBSCRIPTION_CONFIGURATIONS", response.subscriptionConfigurations)
			commit("SET_PAGES", response.paginate)
		} catch (error: any) {
			Sentry.captureException(error)
			dispatch("handleError", { error })
		}
	},
	async getPackageStructureByOrg({ commit }, payload) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		try {
			const packageStructure = await $fetch<{
				[key: number]: ({ [key: number]: number | string } & {
					quantity_threshold: number
				})[]
			}>(`${import.meta.env.VITE_AND_DINE_API}/api/users/subConfig/organisation/${payload}`, config)

			commit("SET_ORG_PACKAGE_CHOICES", packageStructure)
		} catch (error: any) {
			Sentry.captureException(error)
		}
	},
	async getSubConfigById({ commit, dispatch }, payload) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}
		try {
			const response = await $fetch<any[]>(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/subConfig/${payload}`,
				config
			)

			commit("SET_CHOSEN_SUB_CONFIG", response)
		} catch (error: any) {
			Sentry.captureException(error)
			dispatch("handleError", { error })
		}
	},
	async getSubscriptionTypes({ commit }) {
		const config = {
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
		}
		try {
			const response = await $fetch<any[]>(
				`${import.meta.env.VITE_AND_DINE_API}/api/public/subConfig/subscriptionTypes`,
				config
			)

			commit("SET_SUBSCRIPTION_TYPES", response)
		} catch (error: any) {
			Sentry.captureException(error)
		}
	},
	async getPackageTypes({ commit }) {
		const config = {
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
		}
		try {
			const response = await $fetch<any[]>(
				`${import.meta.env.VITE_AND_DINE_API}/api/public/subConfig/packageTypes`,
				config
			)

			commit("SET_PACKAGE_TYPES", response)
		} catch (error: any) {
			Sentry.captureException(error)
		}
	},
	async updateSubConfig({ commit, state, dispatch }, payload) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		try {
			commit("SET_SUB_CONFIG_LOADING", true)

			const subscriptionId =
				state.chosenOrg?.subscription?.id ||
				(await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/organisations/${payload.organisationId}`,
					{ headers: config.headers }
				).then((org) => org.subscription?.id))

			const tasks = (payload.subConfigs || []).map((cfg) => {
				if (cfg.id) {
					return $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/subConfig/${cfg.id}`, {
						method: "PUT",
						body: cfg,
						...config,
					})
				}
				return $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/subConfig`, {
					method: "POST",
					body: { ...cfg, subscription_id: subscriptionId },
					...config,
				})
			})

			await Promise.all(tasks)

			commit("SET_SUB_CONFIG_LOADING", false)
			// payload is { organisationId, subConfigs } from updateSubscription, or
			// the subConfig page's own shape — neither carries `id` or
			// `organisation_id`, so both properties were pruned off every event and
			// sub_config_saved landed unattributed. Read the keys that exist, and
			// report how many configs the save actually covered.
			track(EVENTS.SUB_CONFIG_SAVED, {
				operation: "updated",
				// The subConfig page sends a bare { id, ...fields }; updateSubscription
				// sends { organisationId, subConfigs }. Read both shapes.
				sub_config_id: entityId(payload?.id),
				organisation_id: entityId(payload?.organisationId ?? payload?.organisation_id),
				sub_config_count: Array.isArray(payload?.subConfigs) ? payload.subConfigs.length : 0,
				created_count: Array.isArray(payload?.subConfigs)
					? payload.subConfigs.filter((cfg: any) => !cfg?.id).length
					: 0,
			})
			commit("SET_SUCCESS_MESSAGE", "Successfully updated organisation config")
			setTimeout(() => commit("SET_SUCCESS_MESSAGE", false), 5000)
		} catch (error) {
			commit("SET_SUB_CONFIG_LOADING", false)
			dispatch("handleError", { error, description: "updated configuration" })
			Sentry.captureException(error)
		}
	},
	async createSubConfig({ commit, dispatch }, payload) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		try {
			commit("SET_SUB_CONFIG_LOADING", true)

			const response = await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/subConfig`, {
				method: "POST",
				body: payload,
				...config,
			})

			commit("SET_SUB_CONFIG_LOADING", false)

			track(EVENTS.SUB_CONFIG_SAVED, {
				operation: "created",
				organisation_id: entityId(payload?.organisation_id),
			})
			commit("SET_SUCCESS_MESSAGE", "Successfully created configuration")
			setTimeout(() => {
				commit("SET_SUCCESS_MESSAGE", false)
				navigateTo(`/settings/subConfigs/${response.id}`)
			}, 2000)
		} catch (error) {
			commit("SET_SUB_CONFIG_LOADING", false)
			dispatch("handleError", { error, description: "create configuration" })
			Sentry.captureException(error)
		}
	},
	// Re-reads the order's ACTIVE delivery booking from our own database and writes it to both
	// places the hub keeps a copy, from one response (Notion 1089).
	//
	// **Makes no PedalMe call.** `getDeliveryBooking/:order_id` is a pure DB read, unlike
	// `updateDeliveryTrackingDetails`, which is the operator-forced provider refresh. Polling
	// must use this one — see utils/deliveryStatusPoller.ts.
	//
	// Both surfaces or neither (commitDeliveryBooking): updating one without the other is how
	// the pill ends up reading "In transit" next to a Cancel button that still thinks it is
	// "scheduled".
	//
	// Rejects on a non-2xx so the poller can classify 404 / 401 / transport failures. Nothing
	// is committed on failure, which is what preserves the last-known booking on screen.
	//
	// The order-id guard lives in the action, not the poller: its cancellation token is checked
	// after the fetch resolves, by which point these commits have already run.
	async refreshDeliveryBooking({ commit, rootState }, { order_id }) {
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
		}

		const response = await $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/delivery/getDeliveryBooking/${order_id}`,
			config
		)

		// The endpoint returns the whole order with the active booking nested, and 404s when
		// there is no active one — so a 2xx without `delivery` is not a shape it produces.
		return commitDeliveryBooking(commit, rootState, response)
	},
	async createDeliveryQuote({ commit }, { order_id, vehicle_type, hypercare }) {
		// vehicle_type is the cargo-bike tier the admin chose in the pre-booking modal;
		// the backend validates it and forwards it to PedalMe (it no longer derives the
		// tier from the order value).
		// hypercare (optional) is the order-level priority flag from the same modal;
		// backend persists it onto Order.hypercare in the booking transaction, so it
		// saves as part of booking (omitted -> existing flag untouched).
		const body = JSON.stringify({
			order_id,
			vehicle_type,
			...(typeof hypercare === "boolean" ? { hypercare } : {}),
		})
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "POST",
			body,
		}

		return $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/delivery/createDeliveryQuote`,
			config
		).then((response) => {
			commit("SET_DELIVERY_BOOKING", response)
			// duration_ms here is the PedalMe quote round-trip as the admin felt it.
			track(EVENTS.DELIVERY_QUOTE_CREATED, {
				order_id: entityId(order_id),
				job_id: response?.job_id,
				vehicle_type: enumValue(response?.vehicle_type),
				hypercare: Boolean(response?.hypercare),
				quote_price_gbp: moneyGbp(response?.price),
			})
		})
	},
	async confirmDeliveryBooking({ state, commit }, reqBody) {
		const body = JSON.stringify(reqBody)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		return $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/delivery/confirmDeliveryBooking`,
			config
		).then((res) => {
			// The delivery-side conversion. Quote→confirm ratio is the single number
			// for whether PedalMe pricing is acceptable to the ops team.
			track(EVENTS.DELIVERY_BOOKING_CONFIRMED, {
				order_id: entityId(reqBody?.order_id),
				job_id: reqBody?.job_id,
				vehicle_type: enumValue(state.deliveryBooking?.vehicle_type),
				hypercare: Boolean(state.deliveryBooking?.hypercare),
				quote_price_gbp: moneyGbp(state.deliveryBooking?.price),
				booking_status: enumValue(res?.status),
			})
			incrementPeople({ delivery_bookings_confirmed_count: 1 })
			commit("SET_DELIVERY_BOOKING", {
				...state.deliveryBooking,
				status: res.status,
			})
		})
	},
	// The operator-forced PedalMe refresh (the manual Refresh, and BookDelivery's post-confirm
	// tracking pull). Answers with the whole order and the RE-READ booking, so the row lands on
	// both copies under the same guards as the poller's read — committing tracking_link alone
	// onto root deliveryBooking left the card's pill and the Cancel button on different rows.
	async updateDeliveryTrackingDetails({ commit, rootState }, reqBody) {
		const body = JSON.stringify(reqBody)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		return $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/delivery/updateDeliveryTrackingDetails`,
			config
		).then((response) => commitDeliveryBooking(commit, rootState, response))
	},
	// Cancels the active PedalMe booking for an order (also the single endpoint that
	// silently discards an abandoned draft — the backend closes a `draft` row locally
	// without calling PedalMe, since PedalMe rejects deleting one). PedalMe has no
	// update API, so a real (scheduled) cancel is the first half of a cancel-then-rebook.
	// We deliberately DON'T clear deliveryBooking here — the backend marks the row
	// `cancelled` (the active-row filter is `status <> 'cancelled'`, not a soft-close
	// column) and drops it from order.delivery, so the caller reloads the page to
	// reflect reality (the active-ledger/source-of-truth rule). job_id is read
	// server-side.
	async cancelDeliveryBooking(_ctx, reqBody) {
		const body = JSON.stringify(reqBody)
		const config = {
			headers: {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			},
			method: "PUT",
			body,
		}

		return $fetch(
			`${import.meta.env.VITE_AND_DINE_API}/api/users/delivery/cancelDeliveryBooking`,
			config
		)
	},
	async getServerTime({ commit, dispatch }) {
		await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/serverTime`)
			.then((response) => {
				commit("SET_SERVER_TIME", response)
			})
			.catch((error) => {
				Sentry.captureException(error)
				dispatch("handleError", { error })
			})
	},
}

export const getters = {
	errorMessage: (state) =>
		state.errorMessages.length
			? state.errorMessages[state.errorMessages.length - 1].message
			: false,
	successMessage: (state) =>
		state.successMessages.length
			? state.successMessages[state.successMessages.length - 1].message
			: false,
	makerOptions: (state) => {
		return state.makers.map((maker) => ({
			name: maker.name,
			value: maker.id,
		}))
	},
	cutOffOptions: () => {
		return [
			"00:00",
			"01:00",
			"02:00",
			"03:00",
			"04:00",
			"05:00",
			"06:00",
			"07:00",
			"08:00",
			"09:00",
			"10:00",
			"11:00",
			"12:00",
			"13:00",
			"14:00",
			"15:00",
			"16:00",
			"17:00",
			"18:00",
			"19:00",
			"20:00",
			"21:00",
			"22:00",
			"23:00",
		].map((val) => ({ name: val, value: val }))
	},
	dayBeforeOptions: () => {
		return [1, 2, 3, 4, 5, 6, 7].map((val) => ({ name: val, value: val }))
	},
	userOptions: (state) => {
		return state.users.map((user) => ({
			name: user.email,
			value: user.id,
		}))
	},
	searchByOptions: (state) => {
		if (state.isAdmin) {
			return [
				{ name: "Order Reference", value: "reference" },
				{
					name: "Customer First Name",
					value: "customer_first_name",
				},
				{
					name: "Customer Last Name",
					value: "customer_last_name",
				},
				{ name: "Company", value: "business_name" },
			]
		} else {
			return [
				{ name: "Order Reference", value: "reference" },
				{ name: "Company", value: "business_name" },
			]
		}
	},
	packageTypeOptions: () => {
		return ["Gold", "Silver", "Bronze"].map((status) => ({
			value: status,
			name: status,
		}))
	},
	discountTypeOptions: () => {
		return [
			{ value: "percent", name: "Percent" },
			{ value: "regular", name: "Fixed Amount" },
		]
	},
	customerRolesOptions: () => {
		return ["customer", "sales", "employee", "manager", "owner"].map((type) => ({
			value: type.toLowerCase(),
			name: type,
		}))
	},
	discountUsageOptions: () => {
		return ["Auto", "Regular"].map((usage) => ({
			value: usage.toLowerCase(),
			name: usage,
		}))
	},
}

const generateFilterQuery = (filters, date = undefined) => {
	let filterQuery = ""

	if (filters.length) {
		filterQuery =
			filterQuery +
			"&filter=" +
			encodeURIComponent(
				JSON.stringify(
					filters.map(([key, value, exact, not]) => {
						return { key: key, value: value, exact, not }
					})
				)
			)
	}

	if (date) {
		filterQuery = filterQuery + "&date=" + JSON.stringify(date)
	}
	return filterQuery
}

const store = createStore({
	state,
	mutations: mutations,
	actions: actions,
	getters: getters,
	modules: {
		product: productStoreModule,
		order: orderStoreModule,
		costCentre: costCentreModule,
		addresses: addressesModule,
		settings: settingsModule,
		integrations: integrationsStoreModule,
		xero: xeroStoreModule,
	},
})

export default defineNuxtPlugin((nuxtApp) => {
	nuxtApp.vueApp.use(store)
	// Install the store instance as a plugin
	nuxtApp.provide("store", store)
})
